import React, { useState, useEffect } from 'react';
import { Upload, Check, AlertCircle, Send, FileText, Building, CreditCard, Loader2, RotateCcw, Trash2, Save } from 'lucide-react';
import { submitVendorRegistration, updateVendorRegistration, VendorFormData, UploadedDocuments, getVendorById, downloadFileFromFirebase, getVendorByUid, updateVendorProfileData } from '../firebaseService';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { useVendorAuth } from './VendorAuthProvider';
import VendorDashboardLayout from './VendorDashboardLayout';
import { User } from 'firebase/auth';

interface VendorRegistrationFormProps {
  section?: 'profile' | 'categories' | 'banking' | 'documents-registration' | 'documents-compliance' | 'contact';
}

const VendorRegistrationForm: React.FC<VendorRegistrationFormProps> = ({ section }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { vendor: vendorUser } = useVendorAuth();
  const isDashboardMode = !!section || location.pathname.includes('/vendor/dashboard');
  const [formData, setFormData] = useState({
    // Business Registration Details
    companyName: '',
    telephone: '',
    fax: '',
    email: '',
    registeredAddress: '',
    ownersPartners: '',
    ownerAddress: '',
    registrationNo: '',
    registrationDate: '',
    tinNo: '',
    isLimitedLiability: '',
    paidUpCapital: '',
    lastTurnover: '',
    isISO9001: '',
    
    // Categories
    categories: {
      agriculturalConsulting: false,
      equipmentCalibration: false,
      technicalSupplies: false,
      nonTechnicalSupplies: false,
      insuranceServices: false,
      automobileServices: false,
      cateringServices: false,
      interiorDecoration: false,
      civilElectricalWorks: false,
      manpowerSupply: false,
      others: false,
      othersSpecify: ''
    },
    
    specializationDetails: '',
    extendCredit: '',
    creditVolume: '',
    creditTenure: '',
    paymentPlan: '',
    postDeliveryPayment: '',
    
    // Banking Information
    bankingInfo: [
      { bankName: '', accountType: '', accountNumber: '' }
    ],
    bvn: '',
    bankDiscussionObjection: '',
    bankObjectionReason: '',
    
    // References
    references: [
      { company: '', telNo: '', date: '', location: '', contractValue: '', clientContact: '' }
    ],
    performanceDiscussionObjection: '',
    performanceObjectionReason: '',
    
    // Authorized Representative Details
    authorizedRepresentativeName: '',
    authorizedRepresentativePhone: '',
    authorizedRepresentativeDesignation: '',
    // Contact Person Details
    contactPersonName: '',
    contactPersonPhone: '',
    contactPersonEmail: '',
    declarationAccepted: false
  });

  const [uploadedDocs, setUploadedDocs] = useState({
    applicationLetter: null,
    incorporationCert: null,
    vatCert: null,
    passportPhoto: null,
    previousJobEvidence: null,
    taxClearance: null,
    performanceBond: null,
    referenceLetter: null,
    authorizedRepresentativeSignature: null,
    memat: null,
    identityDocuments: null,
    utilityBill: null,
    cacStatusReport: null,
    // Additional optional uploads
    letterOfIntroduction: null,
    boardResolution: null,
    companyProfile: null,
    taxComplianceCertificate: null,
    exportImportLicense: null,
    cacForm2And7: null,
    iso9001Certificate: null,
    productComplianceCertificates: null,
    hsePolicy: null,
    safetyRecords: null,
    generalLiabilityInsurance: null,
    productLiabilityInsurance: null,
    supplierCodeOfConduct: null,
    antiBriberyPolicyAcknowledgment: null,
    dataProtectionPolicy: null
  });

  const sectionMap: Record<string, number> = {
    'profile': 0,
    'categories': 1,
    'banking': 2,
    'documents-registration': 3,
    'documents-compliance': 4,
    'contact': 5
  };

  const [currentSection, setCurrentSection] = useState(section ? sectionMap[section] : 0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(isDashboardMode);

  const sections = [
    { id: 0, title: 'Business Details', icon: Building, path: '/vendor/dashboard/profile' },
    { id: 1, title: 'Categories & Services', icon: FileText, path: '/vendor/dashboard/categories' },
    { id: 2, title: 'Banking & References', icon: CreditCard, path: '/vendor/dashboard/banking' },
    { id: 3, title: 'Business Registration & Identity', icon: Upload, path: '/vendor/dashboard/documents-registration' },
    { id: 4, title: 'Compliance & Certifications', icon: Upload, path: '/vendor/dashboard/documents-compliance' },
    { id: 5, title: 'Contact Person & Declaration', icon: Check, path: '/vendor/dashboard/contact' }
  ];

  // Sync currentSection with section prop when route changes
  useEffect(() => {
    if (section && sectionMap[section] !== undefined) {
      setCurrentSection(sectionMap[section]);
    }
  }, [section]);

  // Load vendor data on mount
  useEffect(() => {
    const loadVendorData = async () => {
      if (isDashboardMode && vendorUser?.uid) {
        setLoading(true);
        try {
          const result = await getVendorByUid(vendorUser.uid);
          if (result.success && result.vendor) {
            const vendorData = result.vendor as any;
            // Populate form data
            setFormData((prev) => ({
              ...prev,
              ...vendorData,
              // Ensure arrays are initialized
              bankingInfo: vendorData.bankingInfo || prev.bankingInfo,
              references: vendorData.references || prev.references,
              categories: vendorData.categories || prev.categories
            }));
            
            // Load document URLs (we'll show them as uploaded but won't download files)
            if (vendorData.documentUrls) {
              // Mark documents as uploaded (we'll use a special marker)
              const docStatus: any = {};
              Object.keys(vendorData.documentUrls).forEach(key => {
                docStatus[key] = { uploaded: true, url: vendorData.documentUrls[key] };
              });
              // We'll handle this differently - just mark that docs exist
            }
          }
        } catch (error) {
          console.error('Error loading vendor data:', error);
        } finally {
          setLoading(false);
        }
      } else {
        // Legacy: Load cached data
        const cached = localStorage.getItem('vendorFormCache');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed?.formData) {
              setFormData((prev) => ({ ...prev, ...parsed.formData }));
            }
            if (parsed?.vendorId) {
              (async () => {
                const res = await getVendorById(parsed.vendorId);
                if (res.success && (res.vendor as any).documentUrls) {
                  const urls = (res.vendor as any).documentUrls as Record<string, string>;
                  const restored: any = {};
                  for (const [key, url] of Object.entries(urls)) {
                    try {
                      const nameGuess = key.endsWith('Url') ? key.replace(/Url$/, '') : key;
                      const file = await downloadFileFromFirebase(url, `${nameGuess}.pdf`);
                      restored[key.replace(/Url$/, '')] = file;
                    } catch {}
                  }
                  if (Object.keys(restored).length > 0) {
                    setUploadedDocs((prev: any) => ({ ...prev, ...restored }));
                  }
                }
              })();
            }
          } catch {}
        }
        setLoading(false);
      }
    };

    loadVendorData();
  }, [isDashboardMode, vendorUser?.uid]);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as +234 XXX XXX XXXX for Nigerian numbers
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `+${phoneNumber.slice(0, 3)} ${phoneNumber.slice(3)}`;
    } else if (phoneNumber.length <= 9) {
      return `+${phoneNumber.slice(0, 3)} ${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6)}`;
    } else {
      return `+${phoneNumber.slice(0, 3)} ${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6, 9)} ${phoneNumber.slice(9, 13)}`;
    }
  };

  const formatAmount = (value: string) => {
    // Remove all non-digits and decimal points
    const cleanValue = value.replace(/[^\d.]/g, '');
    
    // Add thousand separators
    const parts = cleanValue.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    return parts.join('.');
  };

  const formatAccountNumber = (value: string) => {
    // Remove all non-digits
    return value.replace(/\D/g, '');
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleArrayUpdate = (field: string, index: number, subfield: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev as any)[field].map((item: any, i: number) => 
        i === index ? { ...item, [subfield]: value } : item
      )
    }));
  };

  const addArrayItem = (field: string, template: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev as any)[field], template]
    }));
  };

  const removeArrayItem = (field: string, index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev as any)[field].filter((_: any, i: number) => i !== index)
    }));
  };

  const handleFileUpload = (docType: string, file: File) => {
    const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
    if (file.size > MAX_SIZE) {
      setErrors(prev => ({
        ...prev,
        documents: `File too large for ${docType}. Maximum size is 5 MB.`
      }));
      return;
    }
    setErrors(prev => ({ ...prev, documents: '' }));
    setUploadedDocs(prev => ({
      ...prev,
      [docType]: file
    }));
  };

  const handleFileRemove = (docType: string) => {
    setUploadedDocs(prev => ({
      ...prev,
      [docType]: null
    }));
  };

  const validateSection = (sectionId: number) => {
    const newErrors: Record<string, string> = {};
    
    // Section 0: Business Details
    if (sectionId === 0) {
      if (!formData.companyName?.trim()) newErrors.companyName = 'Company name is required';
      if (!formData.email?.trim()) newErrors.email = 'Email address is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
      if (!formData.telephone?.trim()) newErrors.telephone = 'Phone number is required';
      if (!formData.registeredAddress?.trim()) newErrors.registeredAddress = 'Office address is required';
      if (!formData.ownersPartners?.trim()) newErrors.ownersPartners = 'Name of owners is required';
      if (!formData.ownerAddress?.trim()) newErrors.ownerAddress = 'Address of owners is required';
      if (!formData.registrationNo?.trim()) newErrors.registrationNo = 'Registration number is required';
      if (!formData.tinNo?.trim()) newErrors.tinNo = 'TIN number is required';
    }
    
    // Section 1: Categories & Services
    if (sectionId === 1) {
      // Check if at least one category is selected
      const hasSelectedCategory = Object.entries(formData.categories).some(([key, value]) => 
        key !== 'othersSpecify' && value === true
      );
      if (!hasSelectedCategory) newErrors.categories = 'You must select at least one service category';
      
      if (!formData.specializationDetails?.trim()) newErrors.specializationDetails = 'Areas of specialization is required';
      if (!formData.paymentPlan?.trim()) newErrors.paymentPlan = 'Payment plan is required';
    }
    
    // Section 2: Banking & References
    if (sectionId === 2) {
      // Check if at least one bank is provided with all required fields
      const hasValidBanking = formData.bankingInfo.some(bank => 
        bank.bankName?.trim() && bank.accountType?.trim() && bank.accountNumber?.trim()
      );
      if (!hasValidBanking) newErrors.bankingInfo = 'Banking information is required';
    }
    
    // Section 3: Business Registration & Identity Documents
    if (sectionId === 3) {
      const requiredDocuments = [
        'applicationLetter',
        'incorporationCert', 
        'vatCert',
        'passportPhoto',
        'previousJobEvidence',
        'memat',
        'identityDocuments',
        'utilityBill',
        'cacStatusReport'
      ];
      
      const missingDocuments = requiredDocuments.filter(docType => !uploadedDocs[docType as keyof typeof uploadedDocs]);
      if (missingDocuments.length > 0) {
        newErrors.documents = `Please upload all required documents: ${missingDocuments.join(', ')}`;
      }
    }
    
    // Section 4: Compliance & Certifications (all documents are optional, no validation needed)
    // No validation required for section 4 as all documents are optional
    
    // Section 5: Contact Person & Declaration
    if (sectionId === 5) {
      // Authorized Representative validation
      if (!formData.authorizedRepresentativeName?.trim()) newErrors.authorizedRepresentativeName = 'Authorized representative name is required';
      if (!formData.authorizedRepresentativePhone?.trim()) newErrors.authorizedRepresentativePhone = 'Authorized representative phone is required';
      if (!formData.authorizedRepresentativeDesignation?.trim()) newErrors.authorizedRepresentativeDesignation = 'Authorized representative designation is required';
      if (!uploadedDocs.authorizedRepresentativeSignature) newErrors.authorizedRepresentativeSignature = 'Authorized representative signature is required';
      // Contact Person validation
      if (!formData.contactPersonName?.trim()) newErrors.contactPersonName = 'Contact person name is required';
      if (!formData.contactPersonPhone?.trim()) newErrors.contactPersonPhone = 'Contact person phone is required';
      if (!formData.contactPersonEmail?.trim()) newErrors.contactPersonEmail = 'Contact person email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactPersonEmail)) newErrors.contactPersonEmail = 'Please enter a valid email address';
      if (!formData.declarationAccepted) newErrors.declarationAccepted = 'You must accept the declaration';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextSection = () => {
    if (validateSection(currentSection) && currentSection < sections.length - 1) {
      if (isDashboardMode) {
        // In dashboard mode, navigate to the next section's route
        const nextSection = sections[currentSection + 1];
        navigate(nextSection.path);
      } else {
        // In legacy mode, just update the section state
        setCurrentSection(currentSection + 1);
      }
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      if (isDashboardMode) {
        // In dashboard mode, navigate to the previous section's route
        const prevSection = sections[currentSection - 1];
        navigate(prevSection.path);
      } else {
        // In legacy mode, just update the section state
        setCurrentSection(currentSection - 1);
      }
    }
  };

  const saveSection = async () => {
    if (!vendorUser?.uid) return;
    
    setIsSubmitting(true);
    setSubmitMessage('');
    
    try {
      // Filter documents to only include new uploads (not already uploaded ones)
      const newDocs: Partial<UploadedDocuments> = {};
      Object.entries(uploadedDocs).forEach(([key, value]) => {
        if (value && typeof value === 'object' && 'name' in value) {
          const fileValue = value as any;
          if (fileValue instanceof File) {
            newDocs[key as keyof UploadedDocuments] = fileValue;
          }
        }
      });

      const result = await updateVendorProfileData(
        vendorUser.uid,
        formData as Partial<VendorFormData>,
        Object.keys(newDocs).length > 0 ? newDocs : undefined
      );
      
      if (result.success) {
        setSubmitMessage('Changes saved successfully!');
        setTimeout(() => setSubmitMessage(''), 3000);
      } else {
        setSubmitStatus('error');
        setSubmitMessage(result.error || 'Failed to save changes. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('An unexpected error occurred. Please try again.');
      console.error('Save error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitForm = async () => {
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitMessage('');
    
    try {
      if (isDashboardMode && vendorUser?.uid) {
        // Dashboard mode: save to vendor-profiles
        const newDocs: Partial<UploadedDocuments> = {};
        Object.entries(uploadedDocs).forEach(([key, value]) => {
          if (value && typeof value === 'object' && 'name' in value) {
            const fileValue = value as any;
            if (fileValue instanceof File) {
              newDocs[key as keyof UploadedDocuments] = fileValue;
            }
          }
        });

        const result = await updateVendorProfileData(
          vendorUser.uid,
          formData as Partial<VendorFormData>,
          Object.keys(newDocs).length > 0 ? newDocs : undefined
        );
        
        if (result.success) {
          setSubmitStatus('success');
          setSubmitMessage('Application submitted successfully!');
        } else {
          setSubmitStatus('error');
          setSubmitMessage(result.error || 'Failed to submit application. Please try again.');
        }
      } else {
        // Legacy mode: use old submission flow
        const cached = localStorage.getItem('vendorFormCache');
        let result: { success: boolean; vendorId?: string; error?: string };
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed?.vendorId) {
              result = await updateVendorRegistration(
                parsed.vendorId,
                formData as VendorFormData,
                uploadedDocs as Partial<UploadedDocuments>
              );
            } else {
              result = await submitVendorRegistration(
                formData as VendorFormData,
                uploadedDocs as UploadedDocuments
              );
            }
          } catch {
            result = await submitVendorRegistration(
              formData as VendorFormData,
              uploadedDocs as UploadedDocuments
            );
          }
        } else {
          result = await submitVendorRegistration(
            formData as VendorFormData, 
            uploadedDocs as UploadedDocuments
          );
        }
        
        if (result.success) {
          if (isEditing) {
            setSubmitMessage(`Application updated successfully! Your vendor ID is: ${result.vendorId}`);
            setIsEditing(false);
          } else {
            setSubmitStatus('success');
            setSubmitMessage(`Application submitted successfully! Your vendor ID is: ${result.vendorId}`);
          }

          try {
            const cache = {
              vendorId: result.vendorId,
              formData,
              uploadedDocNames: Object.fromEntries(
                Object.entries(uploadedDocs as any).map(([k, v]) => [k, v ? (v as File).name : null])
              ),
              cachedAt: new Date().toISOString()
            };
            localStorage.setItem('vendorFormCache', JSON.stringify(cache));
          } catch {}

          if (user) {
            setTimeout(() => {
              navigate('/staff/dashboard');
            }, 1500);
          }
        } else {
          setSubmitStatus('error');
          setSubmitMessage(result.error || 'Failed to submit application. Please try again.');
        }
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('An unexpected error occurred. Please try again.');
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBusinessDetails = () => (
    <div className="space-y-6">
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">A. Business Registration Details</label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">Company Name *</label>
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            className={`w-full p-3 border rounded-md ${errors.companyName ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Enter company name"
          />
          {errors.companyName && <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>}
        </div>
        
        <div>
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">Email Address *</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`w-full p-3 border rounded-md ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="company@example.com"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">Telephone Number *</label>
          <input
            type="tel"
            value={formData.telephone}
            onChange={(e) => handleInputChange('telephone', formatPhoneNumber(e.target.value))}
            className={`w-full p-3 border rounded-md ${errors.telephone ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="+234 XXX XXX XXXX"
            maxLength={17}
          />
          {errors.telephone && <p className="text-red-500 text-xs mt-1">{errors.telephone}</p>}
        </div>

        <div>
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">Fax Number</label>
          <input
            type="tel"
            value={formData.fax}
            onChange={(e) => handleInputChange('fax', formatPhoneNumber(e.target.value))}
            className="w-full p-3 border border-gray-300 rounded-md"
            placeholder="+234 XXX XXX XXXX"
            maxLength={17}
          />
        </div>
      </div>

      <div>
        <label className="block text-left text-sm font-medium text-gray-700 mb-2">Registered Office Address *</label>
        <textarea
          value={formData.registeredAddress}
          onChange={(e) => handleInputChange('registeredAddress', e.target.value)}
          rows={3}
          className={`w-full p-3 border rounded-md ${errors.registeredAddress ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Enter complete registered address"
        />
        {errors.registeredAddress && <p className="text-red-500 text-xs mt-1">{errors.registeredAddress}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">Name of Owners/Partners *</label>
          <input
            type="text"
            value={formData.ownersPartners}
            onChange={(e) => handleInputChange('ownersPartners', e.target.value)}
            className={`w-full p-3 border rounded-md ${errors.ownersPartners ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Name of Owners/Partners"
          />
          {errors.ownersPartners && <p className="text-red-500 text-xs mt-1">{errors.ownersPartners}</p>}
        </div>

        <div>
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">Address of Owners/Partners *</label>
          <input
            type="text"
            value={formData.ownerAddress}
            onChange={(e) => handleInputChange('ownerAddress', e.target.value)}
            className={`w-full p-3 border rounded-md ${errors.ownerAddress ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Address of Owners/Partners"
          />
          {errors.ownerAddress && <p className="text-red-500 text-xs mt-1">{errors.ownerAddress}</p>}
        </div>

      </div>

      <label className="block text-left text-sm font-medium text-gray-700 mb-2">Nigeria Registration Data: Companies Decree</label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">Registration Number *</label>
          <input
            type="text"
            value={formData.registrationNo}
            onChange={(e) => handleInputChange('registrationNo', e.target.value)}
            className={`w-full p-3 border rounded-md ${errors.registrationNo ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="RC Number"
          />
          {errors.registrationNo && <p className="text-red-500 text-xs mt-1">{errors.registrationNo}</p>}
        </div>

        <div>
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">Registration Date</label>
          <input
            type="date"
            value={formData.registrationDate}
            onChange={(e) => handleInputChange('registrationDate', e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md"
          />
        </div>

        <div>
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">TIN Number *</label>
          <input
            type="text"
            value={formData.tinNo}
            onChange={(e) => handleInputChange('tinNo', e.target.value)}
            className={`w-full p-3 border rounded-md ${errors.tinNo ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Tax Identification Number"
          />
          {errors.tinNo && <p className="text-red-500 text-xs mt-1">{errors.tinNo}</p>}
        </div>

        <div>
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">Paid Up Capital</label>
          <input
            type="text"
            value={formData.paidUpCapital}
            onChange={(e) => handleInputChange('paidUpCapital', formatAmount(e.target.value))}
            className="w-full p-3 border border-gray-300 rounded-md"
            placeholder="₦ 0.00"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">Limited Liability Company?</label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="isLimitedLiability"
                value="yes"
                checked={formData.isLimitedLiability === 'yes'}
                onChange={(e) => handleInputChange('isLimitedLiability', e.target.value)}
                className="mr-2"
              />
              Yes
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="isLimitedLiability"
                value="no"
                checked={formData.isLimitedLiability === 'no'}
                onChange={(e) => handleInputChange('isLimitedLiability', e.target.value)}
                className="mr-2"
              />
              No
            </label>
          </div>
        </div>

        <div>
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">ISO 9001 Certified?</label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="isISO9001"
                value="yes"
                checked={formData.isISO9001 === 'yes'}
                onChange={(e) => handleInputChange('isISO9001', e.target.value)}
                className="mr-2"
              />
              Yes
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="isISO9001"
                value="no"
                checked={formData.isISO9001 === 'no'}
                onChange={(e) => handleInputChange('isISO9001', e.target.value)}
                className="mr-2"
              />
              No
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCategories = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-left text-sm font-medium text-gray-700 mb-3">Service Categories *</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.keys(formData.categories).filter(key => key !== 'othersSpecify').map((category) => (
            <label key={category} className="flex items-center p-2 border rounded hover:bg-gray-50">
              <input
                type="checkbox"
                checked={(formData.categories as any)[category]}
                onChange={(e) => handleInputChange(`categories.${category}`, e.target.checked)}
                className="mr-3"
              />
              <span className="text-sm">
                {category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </span>
            </label>
          ))}
        </div>
        
        {formData.categories.others && (
          <div className="mt-3">
            <input
              type="text"
              value={formData.categories.othersSpecify}
              onChange={(e) => handleInputChange('categories.othersSpecify', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md"
              placeholder="Please specify other services..."
            />
          </div>
        )}
        {errors.categories && <p className="text-red-500 text-xs mt-2">{errors.categories}</p>}
      </div>

      <div>
        <label className="block text-left text-sm font-medium text-gray-700 mb-2">Areas of Specialization *</label>
        <textarea
          value={formData.specializationDetails}
          onChange={(e) => handleInputChange('specializationDetails', e.target.value)}
          rows={4}
          className={`w-full p-3 border rounded-md ${errors.specializationDetails ? 'border-red-500' : 'border-gray-300'}`}
          placeholder="Provide detailed information about your areas of specialization..."
        />
        {errors.specializationDetails && <p className="text-red-500 text-xs mt-1">{errors.specializationDetails}</p>}
      </div>

      <div>
        <label className="block text-left text-sm font-medium text-gray-700 mb-2">Extend Credit Facilities?</label>
        <div className="flex gap-4 mb-3">
          <label className="flex items-center">
            <input
              type="radio"
              name="extendCredit"
              value="yes"
              checked={formData.extendCredit === 'yes'}
              onChange={(e) => handleInputChange('extendCredit', e.target.value)}
              className="mr-2"
            />
            Yes
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="extendCredit"
              value="no"
              checked={formData.extendCredit === 'no'}
              onChange={(e) => handleInputChange('extendCredit', e.target.value)}
              className="mr-2"
            />
            No
          </label>
        </div>

        {formData.extendCredit === 'yes' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-left text-sm font-medium text-gray-700 mb-2">Credit Volume</label>
              <input
                type="text"
                value={formData.creditVolume}
                onChange={(e) => handleInputChange('creditVolume', formatAmount(e.target.value))}
                className="w-full p-3 border border-gray-300 rounded-md"
                placeholder="₦ 0.00"
              />
            </div>
            <div>
              <label className="block text-left text-sm font-medium text-gray-700 mb-2">Credit Tenure</label>
              <input
                type="text"
                value={formData.creditTenure}
                onChange={(e) => handleInputChange('creditTenure', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md"
                placeholder="e.g., 30 days"
              />
            </div>
          </div>
        )}

        {/* Payment Plan and Post Delivery Payment - Always visible */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-left text-sm font-medium text-gray-700 mb-2">Payment Plan *</label>
            <select
              value={formData.paymentPlan}
              onChange={(e) => handleInputChange('paymentPlan', e.target.value)}
              className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.paymentPlan ? 'border-red-500' : 'border-gray-300'}`}
            >
              <option value="">Select payment plan</option>
              <option value="20%">20% in advance</option>
              <option value="30%">30% in advance</option>
              <option value="50%">50% in advance</option>
              <option value="70%">70% in advance</option>
            </select>
            {errors.paymentPlan && <p className="text-red-500 text-xs mt-1">{errors.paymentPlan}</p>}
          </div>
          <div>
            <label className="block text-left text-sm font-medium text-gray-700 mb-2">Post Delivery Payment</label>
            <select
              value={formData.postDeliveryPayment}
              onChange={(e) => handleInputChange('postDeliveryPayment', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select payment period</option>
              <option value="2 weeks">2 weeks</option>
              <option value="4 weeks">4 weeks</option>
              <option value="6 weeks">6 weeks</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBankingReferences = () => (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center mb-4">
          <label className="block text-left text-sm font-medium text-gray-700">Banking Information *</label>
          <button
            type="button"
            onClick={() => addArrayItem('bankingInfo', { bankName: '', accountType: '', accountNumber: '' })}
            className="px-3 py-1 bg-sunbeth-orange-500 text-white text-sm rounded hover:bg-sunbeth-orange-600"
          >
            Add Bank
          </button>
        </div>
        
        {formData.bankingInfo.map((bank, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-lg">
            <input
              type="text"
              value={bank.bankName}
              onChange={(e) => handleArrayUpdate('bankingInfo', index, 'bankName', e.target.value)}
              placeholder="Bank Name"
              className="p-2 border border-gray-300 rounded"
            />
            <input
              type="text"
              value={bank.accountType}
              onChange={(e) => handleArrayUpdate('bankingInfo', index, 'accountType', e.target.value)}
              placeholder="Account Type"
              className="p-2 border border-gray-300 rounded"
            />
            <div className="flex gap-2">
              <input
                type="text"
                value={bank.accountNumber}
                onChange={(e) => handleArrayUpdate('bankingInfo', index, 'accountNumber', formatAccountNumber(e.target.value))}
                placeholder="Account Number"
                className="flex-1 p-2 border border-gray-300 rounded"
                maxLength={10}
              />
              {formData.bankingInfo.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeArrayItem('bankingInfo', index)}
                  className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
        {errors.bankingInfo && <p className="text-red-500 text-xs mt-2">{errors.bankingInfo}</p>}
      </div>

      {/* BVN Field */}
      <div>
        <label className="block text-left text-sm font-medium text-gray-700 mb-2">
          Bank Verification Number (BVN)
        </label>
        <input
          type="text"
          value={formData.bvn}
          onChange={(e) => handleInputChange('bvn', formatAccountNumber(e.target.value))}
          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Enter your 11-digit BVN"
          maxLength={11}
        />
        <p className="mt-1 text-xs text-gray-500">
          Enter your 11-digit Bank Verification Number
        </p>
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <label className="block text-left text-sm font-medium text-gray-700">Client References</label>
          <button
            type="button"
            onClick={() => addArrayItem('references', { company: '', telNo: '', date: '', location: '', contractValue: '', clientContact: '' })}
            className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
          >
            Add Reference
          </button>
        </div>
        
        {formData.references.map((ref, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 border rounded-lg">
            <input
              type="text"
              value={ref.company}
              onChange={(e) => handleArrayUpdate('references', index, 'company', e.target.value)}
              placeholder="Company Name"
              className="p-2 border border-gray-300 rounded"
            />
            <input
              type="tel"
              value={ref.telNo}
              onChange={(e) => handleArrayUpdate('references', index, 'telNo', formatPhoneNumber(e.target.value))}
              placeholder="+234 XXX XXX XXXX"
              className="p-2 border border-gray-300 rounded"
              maxLength={17}
            />
            <input
              type="date"
              value={ref.date}
              onChange={(e) => handleArrayUpdate('references', index, 'date', e.target.value)}
              className="p-2 border border-gray-300 rounded"
            />
            <input
              type="text"
              value={ref.location}
              onChange={(e) => handleArrayUpdate('references', index, 'location', e.target.value)}
              placeholder="Location"
              className="p-2 border border-gray-300 rounded"
            />
            <input
              type="text"
              value={ref.contractValue}
              onChange={(e) => handleArrayUpdate('references', index, 'contractValue', formatAmount(e.target.value))}
              placeholder="₦ 0.00"
              className="p-2 border border-gray-300 rounded"
            />
            <div className="flex gap-2">
              <input
                type="text"
                value={ref.clientContact}
                onChange={(e) => handleArrayUpdate('references', index, 'clientContact', e.target.value)}
                placeholder="Client Contact"
                className="flex-1 p-2 border border-gray-300 rounded"
              />
              {formData.references.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeArrayItem('references', index)}
                  className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDocumentUploadRegistration = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-yellow-400 mr-2" />
          <div>
            <p className="text-sm text-yellow-700">
              <strong>Business Registration & Identity Documents:</strong> Items marked with * are mandatory for all vendors
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { key: 'applicationLetter', label: 'Application Letter *', required: true },
          { key: 'incorporationCert', label: 'Certificate of Incorporation *', required: true },
          { key: 'memat', label: 'Memorandum and Article of Association (MEMAT) *', required: true },
          { key: 'cacStatusReport', label: 'CAC Status Report *', required: true },
          { key: 'cacForm2And7', label: 'CAC Form 2 & Form 7 (Particulars of Shareholders and Directors)', required: false },
          { key: 'identityDocuments', label: 'Identity Documents of Principal Owners *', required: true },
          { key: 'passportPhoto', label: 'Passport Photograph *', required: true },
          { key: 'utilityBill', label: 'Utility Bill or Proof of Business Address (≤3 months) *', required: true },
          { key: 'vatCert', label: 'VAT Certificate *', required: true },
          { key: 'taxClearance', label: 'Tax Clearance Certificate', required: false },
          { key: 'taxComplianceCertificate', label: 'Tax Compliance Certificate (TCC)', required: false },
          { key: 'referenceLetter', label: 'Reference Letters', required: false },
          { key: 'letterOfIntroduction', label: 'Letter of Introduction / Intent to Engage', required: false },
          { key: 'boardResolution', label: 'Board Resolution Authorizing Contract Execution (if required)', required: false },
          { key: 'companyProfile', label: 'Company Profile / Capability Statement', required: false },
          { key: 'previousJobEvidence', label: 'Previous Job Evidence *', required: true }
        ].map((doc) => (
          <div key={doc.key} className="border rounded-lg p-4">
            <label className="block text-left text-sm font-medium text-gray-700 mb-2">
              {doc.label}
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400">
              <div className="space-y-1 text-center">
                {(uploadedDocs as any)[doc.key] ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center text-green-600 min-w-0">
                      <Check className="h-5 w-5 flex-shrink-0" />
                      <span className="ml-2 text-sm truncate" title={(uploadedDocs as any)[doc.key].name}>{(uploadedDocs as any)[doc.key].name}</span>
                    </div>
                    <div className="flex items-center justify-end gap-3">
                      <label className="inline-flex items-center gap-1 cursor-pointer bg-white rounded-md font-medium text-sunbeth-orange-600 hover:text-sunbeth-orange-700">
                        <RotateCcw className="h-4 w-4" />
                        <span>Replace</span>
                        <input
                          type="file"
                          className="sr-only"
                          onChange={(e) => e.target.files && e.target.files[0] && handleFileUpload(doc.key, e.target.files[0])}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => handleFileRemove(doc.key)}
                        className="inline-flex items-center gap-1 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Remove</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer bg-white rounded-md font-medium text-sunbeth-orange-600 hover:text-sunbeth-orange-500">
                        <span>Upload a file</span>
                        <input
                          type="file"
                          className="sr-only"
                          onChange={(e) => e.target.files && e.target.files[0] && handleFileUpload(doc.key, e.target.files[0])}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">PDF, PNG, JPG up to 5MB</p>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {errors.documents && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <div>
              <p className="text-sm text-red-700">{errors.documents}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderDocumentUploadCompliance = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-blue-400 mr-2" />
          <div>
            <p className="text-sm text-blue-700">
              <strong>Compliance, Certifications & Policies:</strong> Upload relevant compliance documents, certifications, and policy acknowledgments
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { key: 'performanceBond', label: 'Performance Bond', required: false },
          { key: 'exportImportLicense', label: 'Export/Import License (if applicable)', required: false },
          { key: 'iso9001Certificate', label: 'ISO 9001 or other Quality Management certifications', required: false },
          { key: 'productComplianceCertificates', label: 'CE, UL, or relevant Product Compliance Certificates (for machinery or electrical systems)', required: false },
          { key: 'hsePolicy', label: 'Health, Safety & Environmental (HSE) Policy', required: false },
          { key: 'safetyRecords', label: 'Safety Records or Incident History (if required)', required: false },
          { key: 'generalLiabilityInsurance', label: 'General Liability Insurance Certificate', required: false },
          { key: 'productLiabilityInsurance', label: 'Product Liability or Professional Indemnity Insurance', required: false },
          { key: 'supplierCodeOfConduct', label: 'Signed Supplier Code of Conduct', required: false },
          { key: 'antiBriberyPolicyAcknowledgment', label: 'Anti-Bribery and Corruption Policy Acknowledgment', required: false },
          { key: 'dataProtectionPolicy', label: 'Data Protection or Ethics Compliance Policy (if available)', required: false }
        ].map((doc) => (
          <div key={doc.key} className="border rounded-lg p-4">
            <label className="block text-left text-sm font-medium text-gray-700 mb-2">
              {doc.label}
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400">
              <div className="space-y-1 text-center">
                {(uploadedDocs as any)[doc.key] ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center text-green-600 min-w-0">
                      <Check className="h-5 w-5 flex-shrink-0" />
                      <span className="ml-2 text-sm truncate" title={(uploadedDocs as any)[doc.key].name}>{(uploadedDocs as any)[doc.key].name}</span>
                    </div>
                    <div className="flex items-center justify-end gap-3">
                      <label className="inline-flex items-center gap-1 cursor-pointer bg-white rounded-md font-medium text-sunbeth-orange-600 hover:text-sunbeth-orange-700">
                        <RotateCcw className="h-4 w-4" />
                        <span>Replace</span>
                        <input
                          type="file"
                          className="sr-only"
                          onChange={(e) => e.target.files && e.target.files[0] && handleFileUpload(doc.key, e.target.files[0])}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => handleFileRemove(doc.key)}
                        className="inline-flex items-center gap-1 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Remove</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer bg-white rounded-md font-medium text-sunbeth-orange-600 hover:text-sunbeth-orange-500">
                        <span>Upload a file</span>
                        <input
                          type="file"
                          className="sr-only"
                          onChange={(e) => e.target.files && e.target.files[0] && handleFileUpload(doc.key, e.target.files[0])}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500">PDF, PNG, JPG up to 5MB</p>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {errors.documents && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <div>
              <p className="text-sm text-red-700">{errors.documents}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderContactPersonDeclaration = () => (
    <div className="space-y-6">
      <div className="bg-sunbeth-orange-50 border-l-4 border-sunbeth-orange-400 p-4 mb-6">
        <div className="flex">
          <Check className="h-5 w-5 text-sunbeth-orange-400 mr-2" />
          <div>
            <p className="text-sm text-sunbeth-orange-700">
              <strong>Final Step:</strong> Please provide contact person details and accept the declaration to complete your registration.
            </p>
          </div>
        </div>
      </div>

      {/* Authorized Representative & Contact Person Details */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Authorized Representative & Contact Person Details</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-left text-sm font-medium text-gray-700 mb-2">
              Name of Authorized Representative *
            </label>
            <input
              type="text"
              value={formData.authorizedRepresentativeName}
              onChange={(e) => handleInputChange('authorizedRepresentativeName', e.target.value)}
              className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.authorizedRepresentativeName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter full name"
            />
            {errors.authorizedRepresentativeName && (
              <p className="mt-1 text-sm text-red-600">{errors.authorizedRepresentativeName}</p>
            )}
          </div>

          <div>
            <label className="block text-left text-sm font-medium text-gray-700 mb-2">
              Phone Number of Authorized Representative *
            </label>
            <input
              type="tel"
              value={formData.authorizedRepresentativePhone}
              onChange={(e) => handleInputChange('authorizedRepresentativePhone', formatPhoneNumber(e.target.value))}
              className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.authorizedRepresentativePhone ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="+234 XXX XXX XXXX"
              maxLength={17}
            />
            {errors.authorizedRepresentativePhone && (
              <p className="mt-1 text-sm text-red-600">{errors.authorizedRepresentativePhone}</p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">
            Designation of Authorized Representative *
          </label>
          <input
            type="text"
            value={formData.authorizedRepresentativeDesignation}
            onChange={(e) => handleInputChange('authorizedRepresentativeDesignation', e.target.value)}
            className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.authorizedRepresentativeDesignation ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g., Managing Director, CEO, Authorized Representative"
          />
          {errors.authorizedRepresentativeDesignation && (
            <p className="mt-1 text-sm text-red-600">{errors.authorizedRepresentativeDesignation}</p>
          )}
        </div>

        <div className="mt-4">
          <label className="block text-left text-sm font-medium text-gray-700 mb-2">
            Signature of Authorized Representative *
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400">
            <div className="space-y-1 text-center">
              {uploadedDocs.authorizedRepresentativeSignature ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center text-green-600 min-w-0">
                    <Check className="h-5 w-5 flex-shrink-0" />
                    <span className="ml-2 text-sm truncate" title={(uploadedDocs.authorizedRepresentativeSignature as File).name}>{(uploadedDocs.authorizedRepresentativeSignature as File).name}</span>
                  </div>
                  <div className="flex items-center justify-end gap-3">
                    <label className="inline-flex items-center gap-1 cursor-pointer bg-white rounded-md font-medium text-sunbeth-orange-600 hover:text-sunbeth-orange-700">
                      <RotateCcw className="h-4 w-4" />
                      <span>Replace</span>
                      <input
                        type="file"
                        className="sr-only"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => e.target.files && e.target.files[0] && handleFileUpload('authorizedRepresentativeSignature', e.target.files[0])}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => handleFileRemove('authorizedRepresentativeSignature')}
                      className="inline-flex items-center gap-1 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-sunbeth-orange-600 hover:text-sunbeth-orange-500">
                      <span>Upload signature</span>
                      <input
                        type="file"
                        className="sr-only"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => e.target.files && e.target.files[0] && handleFileUpload('authorizedRepresentativeSignature', e.target.files[0])}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">PDF, PNG, JPG up to 5MB</p>
                </>
              )}
            </div>
          </div>
          {errors.authorizedRepresentativeSignature && (
            <p className="mt-1 text-sm text-red-600">{errors.authorizedRepresentativeSignature}</p>
          )}
        </div>

        {/* Contact Person Details */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-4">Contact Person Details</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-left text-sm font-medium text-gray-700 mb-2">
                Name of Contact Person *
              </label>
              <input
                type="text"
                value={formData.contactPersonName}
                onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.contactPersonName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter full name"
              />
              {errors.contactPersonName && (
                <p className="mt-1 text-sm text-red-600">{errors.contactPersonName}</p>
              )}
            </div>

            <div>
              <label className="block text-left text-sm font-medium text-gray-700 mb-2">
                Phone Number of Contact Person *
              </label>
              <input
                type="tel"
                value={formData.contactPersonPhone}
                onChange={(e) => handleInputChange('contactPersonPhone', formatPhoneNumber(e.target.value))}
                className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.contactPersonPhone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="+234 XXX XXX XXXX"
                maxLength={17}
              />
              {errors.contactPersonPhone && (
                <p className="mt-1 text-sm text-red-600">{errors.contactPersonPhone}</p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-left text-sm font-medium text-gray-700 mb-2">
              Email Address of Contact Person *
            </label>
            <input
              type="email"
              value={formData.contactPersonEmail}
              onChange={(e) => handleInputChange('contactPersonEmail', e.target.value)}
              className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.contactPersonEmail ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="contact@example.com"
            />
            {errors.contactPersonEmail && (
              <p className="mt-1 text-sm text-red-600">{errors.contactPersonEmail}</p>
            )}
          </div>
        </div>
      </div>

      {/* Declaration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Declaration</h3>
        
        <div className="bg-gray-50 p-4 rounded-md mb-4">
          <p className="text-sm text-gray-700 leading-relaxed">
            I confirm to the best of my knowledge that the information provided above is correct and accurately reflects facts about my business. I agree that Sunbeth Global Concepts Limited has the right, at any given time, to evaluate, examine and check the information provided therein, and I will inform Sunbeth Global Concepts Limited of any change(s) to the information as soon as they come to my knowledge. I hereby consent to Sunbeth Global Concepts Limited keeping and retaining my data for the period of use and sharing the information provided with its related entities and partners for business and operational purposes.
          </p>
        </div>

        <div className="flex items-start">
          <input
            type="checkbox"
            id="declarationAccepted"
            checked={formData.declarationAccepted}
            onChange={(e) => handleInputChange('declarationAccepted', e.target.checked)}
            className={`mt-1 mr-3 ${errors.declarationAccepted ? 'border-red-500' : ''}`}
          />
          <label htmlFor="declarationAccepted" className="text-sm text-gray-700">
            I have read and accept the above declaration *
          </label>
        </div>
        {errors.declarationAccepted && (
          <p className="mt-1 text-sm text-red-600">{errors.declarationAccepted}</p>
        )}
      </div>
    </div>
  );

  const formContent = (
    <>
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm mb-8 p-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">SGCL Vendor Registration Form</h1>
              <p className="text-gray-600">Complete all sections to register as a vendor</p>
            </div>
            {user && (
              <button
                onClick={() => navigate('/staff/dashboard')}
                className="text-sunbeth-orange-600 hover:text-sunbeth-orange-800 flex items-center"
              >
                <Building className="w-4 h-4 mr-1" />
                View Dashboard
              </button>
            )}
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg shadow-sm mb-8 p-6">
          <div className="overflow-x-auto md:overflow-x-visible">
            <div className="flex items-center gap-4 md:gap-6 w-max md:w-full">
            {sections.map((section, index) => {
              const Icon = section.icon;
              const isActive = currentSection === index;
              const isCompleted = currentSection > index;
              
              return (
                <React.Fragment key={section.id}>
                  <div
                    className={`flex flex-col items-center shrink-0 ${(isEditing || isDashboardMode) ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (isEditing || isDashboardMode) {
                        if (isDashboardMode) {
                          // In dashboard mode, navigate to the route
                          navigate(section.path);
                        } else {
                          // In legacy mode, just set the section
                          setCurrentSection(index);
                          try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
                        }
                      }
                    }}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      isActive ? 'bg-sunbeth-orange-500 text-white' : 
                      isCompleted ? 'bg-green-500 text-white' : 
                      'bg-gray-200 text-gray-600'
                    }`}>
                      {isCompleted ? <Check size={20} /> : <Icon size={20} />}
                    </div>
                    <span className={`text-sm mt-2 ${isActive ? 'text-sunbeth-orange-600 font-medium' : 'text-gray-600'}`}>
                      {section.title}
                    </span>
                  </div>
                  {index < sections.length - 1 && (
                    <div className={`h-0.5 w-16 md:w-24 mx-2 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          {/* Success Screen */}
          {submitStatus === 'success' && (
            <div className="mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-start">
                  <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-green-800">Submission Successful</h3>
                    <p className="mt-1 text-sm text-green-700">{submitMessage}</p>
                    <p className="mt-2 text-sm text-green-700">You can review a summary below and edit if needed. Your data will remain available here for future updates.</p>
                  </div>
                </div>
              </div>

              {/* Brief Summary */}
              <div className="mt-6 border rounded-lg">
                <div className="px-4 py-3 border-b bg-gray-50 rounded-t-lg">
                  <h4 className="text-sm font-medium text-gray-800">Submitted Information (Brief)</h4>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Company Name</div>
                    <div className="font-medium text-gray-900">{formData.companyName || '—'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Email</div>
                    <div className="font-medium text-gray-900 break-all">{formData.email || '—'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Telephone</div>
                    <div className="font-medium text-gray-900">{formData.telephone || '—'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Registered Address</div>
                    <div className="font-medium text-gray-900">{formData.registeredAddress || '—'}</div>
                  </div>
                </div>
                <div className="px-4 py-3 border-t flex justify-end gap-3 bg-gray-50 rounded-b-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitStatus('idle');
                      setIsEditing(true);
                      setCurrentSection(0);
                      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
                    }}
                    className="px-4 py-2 text-sm bg-sunbeth-orange-600 text-white rounded-md hover:bg-sunbeth-orange-700"
                  >
                    Edit Information
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                <div>
                  <p className="text-sm text-red-700">{submitMessage}</p>
                </div>
              </div>
            </div>
          )}

          {submitMessage && submitStatus !== 'success' && submitStatus !== 'error' && (
            <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
              <div className="flex">
                <Check className="h-5 w-5 text-green-400 mr-2" />
                <div>
                  <p className="text-sm text-green-700">{submitMessage}</p>
                </div>
              </div>
            </div>
          )}

          {(submitStatus !== 'success' || isEditing) && (
            <>
              {currentSection === 0 && renderBusinessDetails()}
              {currentSection === 1 && renderCategories()}
              {currentSection === 2 && renderBankingReferences()}
              {currentSection === 3 && renderDocumentUploadRegistration()}
              {currentSection === 4 && renderDocumentUploadCompliance()}
              {currentSection === 5 && renderContactPersonDeclaration()}
            </>
          )}

          {/* Navigation Buttons */}
          {(submitStatus !== 'success' || isEditing) && (
          <div className="flex justify-between mt-8 pt-6 border-t">
            <button
              onClick={prevSection}
              disabled={currentSection === 0}
              className={`px-6 py-2 rounded-md ${
                currentSection === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-500 text-white hover:bg-gray-600'
              }`}
            >
              Previous
            </button>

              {currentSection === sections.length - 1 ? (
              <button
                onClick={submitForm}
                disabled={isSubmitting}
                className={`px-8 py-2 rounded-md flex items-center ${
                  isSubmitting 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={16} />
                    Submitting...
                  </>
                ) : (
                  <>
                      <Send className="mr-2" size={16} />
                      {isEditing ? 'Resubmit Application' : 'Submit Application'}
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={nextSection}
                className="px-6 py-2 bg-sunbeth-orange-600 text-white rounded-md hover:bg-sunbeth-orange-700"
              >
                Next
              </button>
            )}
          </div>
          )}
        </div>
    </>
  );

  if (loading) {
    return isDashboardMode ? (
      <VendorDashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin h-8 w-8 text-sunbeth-orange-600" />
        </div>
      </VendorDashboardLayout>
    ) : (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin h-8 w-8 text-sunbeth-orange-600" />
      </div>
    );
  }

  if (isDashboardMode) {
    return (
      <VendorDashboardLayout>
        <div className="bg-white rounded-lg shadow-sm p-8">
          {submitMessage && submitStatus !== 'success' && submitStatus !== 'error' && (
            <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
              <div className="flex">
                <Check className="h-5 w-5 text-green-400 mr-2" />
                <div>
                  <p className="text-sm text-green-700">{submitMessage}</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                <div>
                  <p className="text-sm text-red-700">{submitMessage}</p>
                </div>
              </div>
            </div>
          )}

          {currentSection === 0 && renderBusinessDetails()}
          {currentSection === 1 && renderCategories()}
          {currentSection === 2 && renderBankingReferences()}
          {currentSection === 3 && renderDocumentUploadRegistration()}
          {currentSection === 4 && renderDocumentUploadCompliance()}
          {currentSection === 5 && renderContactPersonDeclaration()}

          <div className="flex justify-end mt-8 pt-6 border-t">
            <button
              onClick={saveSection}
              disabled={isSubmitting}
              className={`px-6 py-2 rounded-md flex items-center ${
                isSubmitting 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-sunbeth-orange-600 text-white hover:bg-sunbeth-orange-700'
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={16} />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2" size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </VendorDashboardLayout>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {formContent}
      </div>
    </div>
  );
};

export default VendorRegistrationForm;